import { defineStore } from 'pinia';
import { ref } from 'vue';
import type {
  FareCalcTaxBreakdown,
  FareCalcTaxBreakdownCreateRequest,
  FareCalcTaxBreakdownUpdateRequest,
} from '@/api/client/models/index.ts';
import { useApiClient } from '@/hooks/api-hook.ts';
import { toBoolean, toOptionalNumber } from './common.ts';

type FareCalcTaxBreakdownPayload = Partial<FareCalcTaxBreakdown> & Record<string, unknown>;

function buildCreateRequest(data: FareCalcTaxBreakdownPayload): FareCalcTaxBreakdownCreateRequest {
  return {
    breakdown: data.breakdown,
    destination: data.destination,
    origin: data.origin,
  };
}

function buildUpdateRequest(data: FareCalcTaxBreakdownPayload): FareCalcTaxBreakdownUpdateRequest {
  return {
    breakdown: data.breakdown,
    destination: data.destination,
    id: toOptionalNumber(data.id),
    isActive: toBoolean(data.isActive),
    origin: data.origin,
    version: data.version,
  };
}

export const useFareCalcTaxBreakdownsStore = defineStore('fareCalcTaxBreakdowns', () => {
  const apiClient = useApiClient();

  const list = ref<FareCalcTaxBreakdown[]>([]);
  const added = ref<FareCalcTaxBreakdown | null>(null);
  const loading = ref<boolean>(true);

  async function add(data: FareCalcTaxBreakdownPayload): Promise<FareCalcTaxBreakdown | undefined> {
    const response = await apiClient.fareCalcTaxBreakdowns.post(buildCreateRequest(data));
    added.value = response ?? null;
    return response;
  }

  async function update(data: FareCalcTaxBreakdownPayload): Promise<void> {
    await apiClient.fareCalcTaxBreakdowns.put(buildUpdateRequest(data));
  }

  async function get({ showOnlyActive }: { showOnlyActive?: boolean } = {}): Promise<FareCalcTaxBreakdown[]> {
    loading.value = true;

    try {
      const response = await apiClient.fareCalcTaxBreakdowns.get({
        queryParameters: {
          isActive: showOnlyActive,
        },
      });

      list.value = response ?? [];
      return list.value;
    } finally {
      loading.value = false;
    }
  }

  return {
    list,
    added,
    loading,
    add,
    update,
    get,
  };
});
